import StringUtil from "utils/string-util"
import TimeUtil from "utils/time-util"
import React from "react"
import { CREATED_DEAL_BY_LABELS } from 'domains/sales/deals/constants/sales-deals-constants'

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  manual_transfer: "Direct Bank Transfer",
  credit_card: "MIDTRANS / Credit Card",
  midtrans: "MIDTRANS",
  virtual_account: "Virtual Account",
  edc: "EDC",
  edc_bri: "EDC BRI",
  edc_bca: "EDC BCA",
  sold_in_store: "Sold in Store",
}

const CARD_TYPE_LABELS = {
  visa_mastercard_on_us: "Visa/Mastercard on-us",
  visa_mastercard_off_us: "Visa/Mastercard off-us",
  jcb_union_pay: "JCB/Union Pay",
  amex: "Amex",
  debit_on_us: "Debit on-us",
  debit_off_us: "Debit off-us",
}

const normalizeText = (value) => (value || "").toString().trim().toLowerCase()

const titleize = (value) =>
  (value || "")
    .toString()
    .trim()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const formatCreatedDealBy = (deal) => {
  const explicitValue = normalizeText(deal?.created_deal_by)

  if (explicitValue) {
    return CREATED_DEAL_BY_LABELS[explicitValue] || titleize(explicitValue)
  }

  if (
    deal?.is_website === true ||
    normalizeText(deal?.sales_method) === 'self checkout online' ||
    normalizeText(deal?.sales_method) === 'self-checkout online'
  ) {
    return CREATED_DEAL_BY_LABELS.web
  }

  return CREATED_DEAL_BY_LABELS.admin
}

const formatPaymentMethod = (methodType) => {
  const key = normalizeText(methodType)
  return PAYMENT_METHOD_LABELS[key] || titleize(key) || "-"
}

const formatCardType = (cardType) => {
  const key = normalizeText(cardType)

  if (key === "non_amex" || key === "non amex") return "Non Amex"

  return CARD_TYPE_LABELS[key] || titleize(key)
}

const shouldShowCardType = (methodType, cardType) => {
  const methodKey = normalizeText(methodType)
  const cardKey = normalizeText(cardType)

  return ["credit_card", "edc", "edc_bri", "edc_bca"].includes(methodKey) && cardKey !== ""
}

const OutboundDealsUrlParamsTableCellService = {
  /**
   * Generate cell content for OutboundDealsUrlParamsTable
   * @param {object} deal - Deal payload (includes payment, user.detail, shipment, sku_list)
   */
  generateDealCellData: (deal) => {
    const payment = deal?.payment || {}
    const detail = deal?.user?.detail || deal?.user_detail || {}

    // Prefer backend-provided sku_list; fallback to cart_items if present
    const skus =
      (Array.isArray(deal.sku_list) && deal.sku_list.length > 0
        ? deal.sku_list
        : (deal.cart?.cart_items || [])
            .map((ci) => ci?.product?.sku)
            .filter(Boolean)) || []

    const skuContent =
      skus.length > 0
        ? (
            <div style={{ whiteSpace: "pre-line", lineHeight: "1.25rem" }}>
              {skus.join("\n")}
            </div>
          )
        : "-"

    const authenticationSummary = Array.isArray(deal.authentication_status_summary)
      ? deal.authentication_status_summary
      : []

    const authenticationContent = authenticationSummary.length > 0
      ? (
          <div style={{ whiteSpace: "pre-line", lineHeight: "1.25rem" }}>
            {authenticationSummary.join("\n")}
          </div>
        )
      : "-"

    const paymentMethodContent =
      payment?.method_type
        ? (
            <div style={{ lineHeight: "1.25rem" }}>
              <div>{formatPaymentMethod(payment.method_type)}</div>

              {shouldShowCardType(payment.method_type, payment.card_type) ? (
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {formatCardType(payment.card_type)}
                </div>
              ) : null}
            </div>
          )
        : "-"

    const assistEditCutoffLocked = !!deal?.permissions?.assist_edit_cutoff_locked
    const assistEditTemporaryBlackout = !!deal?.permissions?.assist_edit_temporary_blackout

    const dealStateContent = (
      <div style={{ lineHeight: "1.25rem" }}>
        <div>{deal?.state || "-"}</div>
        {assistEditCutoffLocked && (
          <span
            className="badge badge-warning mt-1"
            title={deal?.permissions?.lock_reason || undefined}
          >
            {assistEditTemporaryBlackout ? "Monthly Cut Off" : "Assist By Locked"}
          </span>
        )}
      </div>
    )

    return {
      id: {
        content: payment?.id ?? deal?.id ?? "-"
      },
      "deals.id": {
        content: deal?.id ?? "-"
      },
      "deals.created_at": {
        content: deal?.created_at
          ? TimeUtil.format(deal.created_at, "DD MMM YY, HH:mm:ss")
          : "-"
      },
      "payments.created_at": {
        content: payment?.created_at
          ? TimeUtil.format(payment.created_at, "DD MMM YY, HH:mm:ss")
          : "-"
      },
      "payments.paid_at": {
        content: payment?.paid_at
          ? TimeUtil.format(payment.paid_at, "DD MMM YY, HH:mm:ss")
          : "-"
      },
      "deals.pickup_at": {
        content: deal.pickup_at ? TimeUtil.format(deal.pickup_at, "DD MMM YY") : "-"
      },
      "deals.reference_number": {
        content: deal.reference_number
      },
      "deals.created_deal_by": {
        content: formatCreatedDealBy(deal)
      },
      "users.email": {
        content: deal?.user?.email || "-"
      },
      "user_details.name": {
        content: detail?.name || "-"
      },
      "user_details.phone": {
        content: detail?.phone || "-"
      },
      "products.sku_list": {
        content: skuContent
      },
      "deals.final_payment": {
        content: StringUtil.currencyCase(deal.final_payment)
      },
      "deals.total_quantity": {
        content: deal.total_quantity ?? "-"
      },
      "deals.authentication_status_summary": {
        content: authenticationContent
      },
      "deals.down_payment": {
        content: deal.down_payment ? StringUtil.currencyCase(deal.down_payment) : "-"
      },
      "payments.method_type": {
        content: paymentMethodContent
      },
      "deals.state": {
        content: dealStateContent
      },
      "deals.note": {
        content: deal.note
      },
    }
  }
}

export default OutboundDealsUrlParamsTableCellService
